import React from 'react';
import { Scale, PenLine, Shield, Ban, Lock, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const NumberBadge = ({ n, color }) => (
  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-sm font-bold ${color}`}>
    {n}
  </span>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/40 via-white to-pink-50/30">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-12 pt-24">
        {/* Icon + Title */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
            <Scale className="w-7 h-7 text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-purple-700">
            Terms and Conditions
          </h1>
          <p className="text-gray-500 text-sm mb-1">Last Updated: March 2026</p>
          <p className="text-gray-500 text-sm">Legally binding agreement governed by Indian Law</p>
        </div>

        {/* Legally Binding Agreement */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8 flex items-start gap-3">
          <PenLine className="w-5 h-5 text-red-700 flex-shrink-0 mt-1" />
          <div>
            <p className="font-bold text-red-700 mb-2 uppercase text-sm">Legally Binding Agreement</p>
            <p className="text-red-800 text-sm leading-relaxed uppercase mb-3">
              BY ACCESSING, BROWSING, OR USING THE KOPARTNER PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS, OUR PRIVACY POLICY, REFUND POLICY, AND CODE OF CONDUCT. IF YOU DO NOT AGREE TO ALL TERMS, DO NOT USE THIS PLATFORM.
            </p>
            <p className="text-red-800 text-sm leading-relaxed">
              These Terms constitute a legally binding agreement between you and SET INDIA BUSINESS PVT LTD under the Information Technology Act, 2000 and Indian Contract Act, 1872.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* 1. Definitions */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <NumberBadge n={1} color="bg-purple-100 text-purple-700" />
              Definitions and Interpretation
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p><strong>"Platform"</strong> means the KoPartner website (kopartner.in), mobile applications, APIs, and all related services operated by SET INDIA BUSINESS PVT LTD (CIN: XXXXXXXXXXXX), a company incorporated under the Companies Act, 2013.</p>
              <p><strong>"User"</strong> means any natural person who accesses or uses the Platform, including but not limited to Clients and KoPartner Service Providers.</p>
              <p><strong>"Client"</strong> means a User who seeks or books social support services through the Platform.</p>
              <p><strong>"KoPartner"</strong> means a User who is registered to provide social support services through the Platform.</p>
              <p><strong>"Services"</strong> means all professional social support services facilitated through the Platform including Elder Care, Hangingout, Clubbing, Movie Partner, Shopping Buddy, Medical Support, Travel Partner, and Domestic Help.</p>
              <p><strong>"Content"</strong> means all text, images, videos, audio, data, and any other material uploaded, posted, or transmitted through the Platform.</p>
            </div>
          </section>

          {/* 2. Nature of Platform */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-blue-800 flex items-center gap-3">
              <NumberBadge n={2} color="bg-blue-100 text-blue-700" />
              Nature of Platform - Intermediary Status
            </h2>
            <p className="text-blue-900 text-sm mb-3">
              Pursuant to the Information Technology Act, 2000, and Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021:
            </p>
            <ul className="list-disc list-inside space-y-1 text-blue-900 text-sm pl-1 mb-3">
              <li>KoPartner operates as an <strong>"intermediary"</strong> as defined under Section 2(1)(w) of the IT Act, 2000</li>
              <li>The Platform merely facilitates connections between Users and does NOT participate in any transaction or service delivery</li>
              <li>KoPartner is NOT an employer, agent, or representative of any User</li>
              <li>KoPartner does NOT own, operate, or control any services listed on the Platform</li>
              <li>All services are provided by independent KoPartners who are NOT employees of KoPartner</li>
            </ul>
            <p className="text-blue-900 text-sm font-semibold">
              KoPartner expressly disclaims any liability for the acts, omissions, errors, representations, warranties, breaches, or negligence of any User or for any personal injuries, death, property damage, or other damages resulting from the use of Services.
            </p>
          </div>

          {/* 3. KoPartner Selection */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-amber-800 flex items-center gap-3">
              <NumberBadge n={3} color="bg-amber-100 text-amber-700" />
              KoPartner Selection - User's Sole Responsibility
            </h2>
            <p className="text-amber-900 text-sm font-bold uppercase border-l-4 border-amber-500 pl-4 mb-4">
              THE SELECTION OF A KOPARTNER IS ENTIRELY BASED ON THE CUSTOMER'S PERSONAL PREFERENCE, REQUIREMENTS, LOCATION, AVAILABILITY, PROFILE INFORMATION, REVIEWS, AND OTHER FACTORS. WE DO NOT INFLUENCE OR CONTROL THE CUSTOMER'S CHOICE IN ANY MANNER.
            </p>
            <p className="text-amber-900 text-sm mb-2">By selecting a KoPartner, the Client irrevocably acknowledges and agrees that:</p>
            <ul className="list-disc list-inside space-y-1 text-amber-900 text-sm pl-1 mb-4">
              <li>The selection was made independently, voluntarily, and without any influence, coercion, or recommendation from KoPartner</li>
              <li>The Client has independently reviewed the KoPartner's profile, ratings, reviews, and service descriptions</li>
              <li>The Client has assessed the suitability of the KoPartner for their specific requirements</li>
              <li>The Client assumes full responsibility for their selection and its consequences</li>
              <li>KoPartner bears NO responsibility for the Client's satisfaction or dissatisfaction with the selected KoPartner</li>
              <li>Any disputes arising from the selection are solely between the Client and the KoPartner</li>
            </ul>
            <div className="bg-amber-100 border border-amber-300 rounded-xl p-4">
              <p className="text-amber-900 text-sm font-bold">
                KOPARTNER EXPLICITLY DISCLAIMS ALL LIABILITY FOR THE BEHAVIOR, CONDUCT, QUALITY OF SERVICE, OR ANY OTHER ASPECT OF THE KOPARTNER SELECTED BY THE CLIENT.
              </p>
            </div>
          </div>

          {/* 4. Limitation of Liability */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-red-700 flex items-center gap-3">
              <NumberBadge n={4} color="bg-red-100 text-red-700" />
              LIMITATION OF LIABILITY - COMPREHENSIVE DISCLAIMER
            </h2>
            <p className="text-red-800 text-sm font-bold uppercase mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, KOPARTNER, SET INDIA BUSINESS PVT LTD, ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AFFILIATES, SUCCESSORS, AND ASSIGNS SHALL NOT BE LIABLE FOR ANY OF THE FOLLOWING:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-red-100/60 rounded-xl p-4">
                <p className="font-semibold text-red-800 text-sm mb-2">Direct Damages</p>
                <ul className="list-disc list-inside space-y-1 text-red-800 text-sm pl-1">
                  <li>Personal injury or death</li>
                  <li>Property damage or loss</li>
                  <li>Financial loss or harm</li>
                  <li>Emotional or psychological distress</li>
                </ul>
              </div>
              <div className="bg-red-100/60 rounded-xl p-4">
                <p className="font-semibold text-red-800 text-sm mb-2">Indirect Damages</p>
                <ul className="list-disc list-inside space-y-1 text-red-800 text-sm pl-1">
                  <li>Consequential damages</li>
                  <li>Incidental damages</li>
                  <li>Special or punitive damages</li>
                  <li>Loss of profits or revenue</li>
                </ul>
              </div>
            </div>
            <ul className="list-disc list-inside space-y-1 text-red-800 text-sm pl-1 mb-4">
              <li>Any acts, omissions, conduct, or behavior of any User</li>
              <li>Any disputes, conflicts, or disagreements between Users</li>
              <li>Any criminal acts, misconduct, negligence, or fraud by any User</li>
              <li>Quality, safety, legality, or suitability of any services</li>
              <li>Accuracy, completeness, or reliability of any User information</li>
              <li>Any technical failures, errors, or interruptions of the Platform</li>
              <li>Any unauthorized access to User data or accounts</li>
              <li>Any third-party actions or claims</li>
            </ul>
            <div className="bg-red-100 border border-red-300 rounded-xl p-4">
              <p className="text-red-800 text-sm font-bold">
                MAXIMUM LIABILITY CAP: IN NO EVENT SHALL KOPARTNER'S TOTAL LIABILITY EXCEED THE LESSER OF (A) THE AMOUNT PAID BY YOU FOR THE SPECIFIC SERVICE IN QUESTION, OR (B) INR 1,000 (RUPEES ONE THOUSAND ONLY).
              </p>
            </div>
          </div>

          {/* 5. Indemnification */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <NumberBadge n={5} color="bg-purple-100 text-purple-700" />
              Indemnification
            </h2>
            <p className="text-gray-700 text-sm mb-3">
              You agree to indemnify, defend, and hold harmless KoPartner, SET INDIA BUSINESS PVT LTD, its directors, officers, employees, agents, affiliates, licensors, and service providers from and against any and all claims, damages, obligations, losses, liabilities, costs, debts, and expenses (including but not limited to attorney's fees) arising from:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1 mb-3">
              <li>Your access to or use of the Platform</li>
              <li>Your violation of these Terms or any applicable law</li>
              <li>Your violation of any third-party rights</li>
              <li>Any dispute between you and another User</li>
              <li>Your Content or any activity in your account</li>
              <li>Your negligent, willful, or wrongful conduct</li>
            </ul>
            <p className="text-gray-600 text-sm">
              This indemnification obligation shall survive the termination of these Terms and your use of the Platform.
            </p>
          </section>

          {/* 6. Payment Terms & Cyber Fraud Disclaimer */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-green-800 flex items-center gap-3">
              <Lock className="w-5 h-5 text-green-700" />
              6. Payment Terms &amp; Cyber Fraud Disclaimer
            </h2>
            <p className="text-green-900 text-sm font-bold mb-3">IMPORTANT DECLARATION REGARDING PAYMENTS:</p>
            <div className="bg-green-100/60 rounded-xl p-4 mb-4">
              <ul className="list-disc list-inside space-y-2 text-green-900 text-sm pl-1">
                <li><strong>ALL PAYMENTS ARE VOLUNTARY:</strong> Every payment made on the Platform is made at the User's own discretion, choice, and free will</li>
                <li><strong>NO PAYMENT DATA STORAGE:</strong> KoPartner does NOT collect, store, process, or retain any payment card numbers, CVV, bank account details, UPI PINs, or any financial credentials</li>
                <li><strong>THIRD-PARTY PROCESSING:</strong> All payments are processed through secure, third-party payment gateways or direct UPI transfers</li>
                <li><strong>QR-BASED PAYMENTS:</strong> Payments are made by scanning a QR code and paying directly to SET INDIA BUSINESS PVT LTD</li>
                <li><strong>AI VERIFICATION:</strong> Payment verification is done through AI-powered screenshot analysis</li>
              </ul>
            </div>
            <div className="bg-green-100 border border-green-300 rounded-xl p-4 mb-3">
              <p className="text-green-900 text-sm font-semibold">
                CYBER FRAUD DISCLAIMER: Since KoPartner does NOT collect, store, or process any financial information whatsoever, Users acknowledge and agree that NO CLAIMS OF CYBER FRAUD, ONLINE FRAUD, PHISHING, OR ANY OTHER FINANCIAL CRIME can be made against KoPartner. All payments are voluntary transactions made directly by Users through their own banking applications.
              </p>
            </div>
            <p className="text-green-900 text-sm">
              By making any payment, you confirm that the payment is your own voluntary act, made with full knowledge of the service being purchased, and you waive any right to claim the transaction as fraudulent, unauthorized, or coerced.
            </p>
          </div>

          {/* 7. KoPartner Membership */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <NumberBadge n={7} color="bg-purple-100 text-purple-700" />
              KoPartner Membership - Non-Refundable
            </h2>
            <p className="text-gray-700 text-sm mb-3">To become a KoPartner Service Provider, users must purchase a membership:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1 mb-4">
              <li><strong>6 Months:</strong> ₹199 + 18% GST = ₹235</li>
              <li><strong>1 Year:</strong> ₹499 + 18% GST = ₹589</li>
              <li><strong>Lifetime:</strong> ₹999 + 18% GST = ₹1,179</li>
            </ul>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-700 text-sm font-bold">
                MEMBERSHIP FEES ARE STRICTLY NON-REFUNDABLE UNDER ANY CIRCUMSTANCES WHATSOEVER. By purchasing a membership, you irrevocably acknowledge and accept that no refunds will be issued for any reason including but not limited to: change of mind, dissatisfaction, account suspension, voluntary discontinuation, failure to receive bookings, personal circumstances, technical issues, or any other reason.
              </p>
            </div>
          </section>

          {/* 8. Service Bookings & Refund Policy */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <NumberBadge n={8} color="bg-purple-100 text-purple-700" />
              Service Bookings &amp; Refund Policy
            </h2>
            <p className="font-semibold text-gray-800 text-sm mb-2">Cancellation Policy:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1 mb-4">
              <li>Cancellation 24+ hours before: 100% refund to KoPartner Wallet</li>
              <li>Cancellation 12-24 hours before: 50% refund to KoPartner Wallet</li>
              <li>Cancellation less than 12 hours or no-show: No refund</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 text-sm font-bold">
                ALL REFUNDS ARE CREDITED TO THE KOPARTNER WALLET ONLY. Wallet balance can only be used for future bookings on the Platform. NO CASH REFUNDS, BANK TRANSFERS, OR UPI REFUNDS WILL BE PROCESSED UNDER ANY CIRCUMSTANCES.
              </p>
            </div>
          </section>

          {/* 9. Prohibited Activities */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <Ban className="w-5 h-5 text-red-600" />
              9. Prohibited Activities
            </h2>
            <p className="text-gray-700 text-sm mb-3">Users are strictly prohibited from:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1">
              <li>Using the Platform for any illegal, immoral, unethical, or unlawful purposes</li>
              <li>Engaging in any romantic, sexual, dating, or intimate activities</li>
              <li>Providing false, misleading, or fraudulent information</li>
              <li>Harassing, threatening, abusing, or stalking other Users</li>
              <li>Bypassing Platform fees or payment systems</li>
              <li>Sharing contact information to circumvent Platform</li>
              <li>Creating multiple or fake accounts</li>
              <li>Using automated systems, bots, or scrapers</li>
              <li>Violating any applicable laws including the IT Act, 2000</li>
              <li>Infringing intellectual property rights</li>
              <li>Transmitting malware, viruses, or harmful code</li>
              <li>Attempting unauthorized access to the Platform or other accounts</li>
            </ul>
          </section>

          {/* 10. IT Act Compliance */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-indigo-800 flex items-center gap-3">
              <Shield className="w-5 h-5 text-indigo-700" />
              10. Information Technology Act, 2000 Compliance
            </h2>
            <p className="text-indigo-900 text-sm mb-3">
              KoPartner operates in compliance with the Information Technology Act, 2000 and its amendments, including:
            </p>
            <ul className="list-disc list-inside space-y-1 text-indigo-900 text-sm pl-1 mb-3">
              <li><strong>Section 79:</strong> Exemption of intermediary liability for third-party content</li>
              <li><strong>IT (Intermediary Guidelines) Rules, 2021:</strong> Due diligence requirements</li>
              <li><strong>IT (Reasonable Security Practices) Rules, 2011:</strong> Data protection standards</li>
            </ul>
            <p className="text-indigo-900 text-sm mb-3">
              Users agree that in accordance with Section 79 of the IT Act, KoPartner as an intermediary is not liable for any information, data, or communication link made available by third parties (Users) using the Platform.
            </p>
            <p className="text-indigo-900 text-sm font-semibold">Grievance Officer: grievance@kopartner.in</p>
          </div>

          {/* 11. Governing Law */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <NumberBadge n={11} color="bg-purple-100 text-purple-700" />
              Governing Law &amp; Dispute Resolution
            </h2>
            <p className="text-gray-700 text-sm mb-2">These Terms shall be governed by and construed in accordance with the laws of India, including:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1 mb-4">
              <li>Information Technology Act, 2000</li>
              <li>Indian Contract Act, 1872</li>
              <li>Consumer Protection Act, 2019</li>
              <li>Any other applicable laws and regulations</li>
            </ul>
            <p className="font-semibold text-gray-800 text-sm mb-2">Dispute Resolution:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1">
              <li>Parties agree to attempt good-faith negotiation before legal proceedings</li>
              <li>Any unresolved disputes shall be referred to arbitration under the Arbitration and Conciliation Act, 1996</li>
              <li>The seat of arbitration shall be New Delhi, India</li>
              <li>Courts in New Delhi shall have exclusive jurisdiction</li>
            </ul>
          </section>

          {/* 12. Modification of Terms */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <NumberBadge n={12} color="bg-purple-100 text-purple-700" />
              Modification of Terms
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              KoPartner reserves the right to modify, amend, or update these Terms at any time without prior notice. Your continued use of the Platform after any modifications constitutes acceptance of the updated Terms. It is your responsibility to review these Terms periodically.
            </p>
          </section>

          {/* 13. Severability & Waiver */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
              <NumberBadge n={13} color="bg-purple-100 text-purple-700" />
              Severability &amp; Waiver
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              If any provision of these Terms is held to be invalid, illegal, or unenforceable, such provision shall be struck and the remaining provisions shall continue in full force and effect.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.
            </p>
          </section>

          {/* Contact Information */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Contact Information</h2>
            <p className="font-semibold text-gray-800 mb-2">SET INDIA BUSINESS PVT LTD</p>
            <p className="text-gray-700 text-sm">Legal Queries: legal@kopartner.in</p>
            <p className="text-gray-700 text-sm">Grievance Officer: grievance@kopartner.in</p>
            <p className="text-gray-700 text-sm">Website: www.kopartner.in</p>
          </div>

          {/* Acknowledgment and Acceptance */}
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-8">
            <h2 className="text-lg font-bold mb-3 text-purple-700 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              Acknowledgment and Acceptance
            </h2>
            <p className="text-purple-900 text-sm uppercase leading-relaxed">
              BY CLICKING "I AGREE," "ACCEPT," OR BY ACCESSING OR USING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE LEGALLY BOUND BY THESE TERMS AND CONDITIONS IN THEIR ENTIRETY. YOU FURTHER ACKNOWLEDGE THAT THESE TERMS CONSTITUTE A LEGALLY BINDING AGREEMENT BETWEEN YOU AND SET INDIA BUSINESS PVT LTD, ENFORCEABLE UNDER INDIAN LAW.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsPage;
